import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { getVarsomeHost } from "../../../../../state/selectors/general.selectors";
import CnvLink from "../../../components/TooltipActions/CnvLink";
import ClinicalSampleCnv from "../../../../../models/ClinicalSampleCnv";
import Zygosity from "../../../components/Zygosity/Zygosity";
import Track from "../../../models/Track";
import getBaseLen from "../../../helpers/getBaseLen";
import getPrettyNumberFormat from "../../../helpers/getPrettyNumberFormat";
import BreakendInfo from "./BreakendInfo";
import SequenceView from "./SequenceView";
import {
  pathogenicityIndex,
  pathogenicityColors,
  pathogenicityLabelsLong,
  NOT_APPLICABLE,
} from "../../../constants/pathogenicity";
import RepeatExpansionInfo from "./RepeatExpansionInfo";

const columnCl =
  "tw-pt-6 tw-relative tw-flex-shrink-0 tw-flex-grow-333 tw-border-r border-normal";
const colCl =
  "tw-flex-shrink-0 tw-w-full tw-h-8 tw-border-b border-normal tw-flex tw-items-center tw-px-1";
const headerCl =
  "tw-absolute tw-truncate tw-top-0 tw-inset-x-0 tw-border-b tw-border-t border-normal tw-p-1 tw-pr-0 tw-overflow-hidden tw-font-semibold tw-bg-blueGray-400 tw-bg-opacity-30 color-2";

function CnvTitle(props) {
  const { item, actions, varsomeHost, track, requestClose } = props;
  const isRulesHidden = pathogenicityIndex[NOT_APPLICABLE] === item.pathoIndex;
  const pathoLabel = pathogenicityLabelsLong[item.pathoIndex];
  const backgroundColor = pathogenicityColors[item.pathoIndex];
  const acmgRules = (item.acmgRules || []).map((x) => `${x[0]} ${x[1]}`);
  const analyses = Object.values(item.analysisDict);
  const { allAnalysesDict } = track.getState();

  function getQualityText(variantItem) {
    if (variantItem.passesAll) return "Passes all";
    if (variantItem.failsAll) return "Fails all";
    return "Fails some";
  }

  return (
    <div className="tw-px-1 tw-py-px tw-max-h-80">
      <div className="tw-flex tw-items-center tw-flex-no-wrap">
        <div className="tw-flex-grow tw-whitespace-no-wrap">
          <CnvLink host={varsomeHost} item={item} />
        </div>
        <div className="tw-w-6" />
        {actions}
      </div>

      <div className="tw-border-b border-normal tw-mb-2" />

      <div className="tooltip-row">
        <div className="tooltip-label tw-w-32">SV Type</div>
        <div className="tw-max-w-80 tw-capitalize">{item.type}</div>
      </div>

      <div className="tooltip-row tw-items-center">
        <div className="tooltip-label tw-w-32">Germline Class</div>
        <div>{pathoLabel}</div>
        <div className="tw-w-2" />
        <div
          className="tw-w-3 tw-h-3 tw-rounded-full"
          style={{ backgroundColor }}
        />
      </div>

      {isRulesHidden ? null : (
        <div className="tooltip-row">
          <div className="tooltip-label tw-w-32">Germline Rules</div>
          <div className="tw-max-w-80">{acmgRules.join(",  ") || "-"}</div>
        </div>
      )}

      <div className="tooltip-row">
        <div className="tooltip-label tw-w-32">Length</div>
        <div className="tw-max-w-80">{getBaseLen(item.region.size)}</div>
      </div>
      <div className="tooltip-row">
        <div className="tooltip-label tw-w-32">Position</div>
        <div className="tw-max-w-80">{item.region.readableStart || "-"}</div>
      </div>

      {item.alt ? (
        <div className="tooltip-row">
          <div className="tooltip-label tw-w-32">ALT Sequence </div>
          <div className=" tw-whitespace-normal tw-max-w-80">
            <SequenceView sequence={item.alt} />
          </div>
        </div>
      ) : null}

      {item.isBreakend ? (
        <BreakendInfo item={item} requestClose={requestClose} track={track} />
      ) : null}

      {item.isRepeatExpansion ? <RepeatExpansionInfo item={item} /> : null}

      <div className="tw-h-2" />

      <div className="color-3 tw-w-96 tw-text-2xs tw-flex tw-overflow-hidden">
        <div className="tw-pt-6 tw-relative tw-flex-grow-333 tw-border-l tw-border-r border-normal tw-overflow-hidden">
          <div className={headerCl}>Sample Name</div>
          {analyses.map((x) => (
            <div
              key={`${x.analysis}-${x.sample}`}
              data-tooltip={`Sample Name: ${x.sample}`}
              data-tooltip-class="genome-browser-small-tooltip"
              className={colCl}
            >
              <div className="tw-min-w-0 tw-truncate tw-font-bold">
                <a href={x.url}>{x.sample}</a>
              </div>
            </div>
          ))}
        </div>
        <div className="tw-pt-6 tw-relative tw-flex-grow-333  tw-border-r border-normal tw-overflow-hidden">
          <div
            className={headerCl}
            data-tooltip="Analysis"
            data-tooltip-class="genome-browser-small-tooltip"
          >
            Analysis
          </div>
          {analyses.map((x) => {
            const key = `${x.analysis}-${x.sample}`;
            const obj = allAnalysesDict.get(key);
            const date = x.createdAt.toLocaleString("en-GB");
            return x.analysis === window.resultsConfig?.analysis_id ? (
              <div key={key} className={colCl}>
                {date}
              </div>
            ) : (
              <div key={key} className={colCl}>
                <div
                  data-tooltip={`Analysis run on: ${date}`}
                  data-tooltip-class="genome-browser-small-tooltip"
                  className="tw-min-w-0 tw-truncate tw-font-bold"
                >
                  <a
                    href={`${window.resultsConfig.baseUrl}${obj.analysisUrl}?variant_id=${item.region.clinicalSearch}`}
                    target="_blank"
                  >
                    {date}
                  </a>
                </div>
              </div>
            );
          })}
        </div>

        {item.isCNV ? (
          <>
            <div className={columnCl}>
              <div
                className={headerCl}
                data-tooltip="Copy Number"
                data-tooltip-class="genome-browser-small-tooltip"
              >
                Copy Number
              </div>
              {analyses.map((x) => (
                <div
                  data-tooltip={`Copy Number: ${x.copyNumber}`}
                  data-tooltip-class="genome-browser-small-tooltip"
                  className={colCl}
                  key={`${x.analysis}-${x.sample}`}
                >
                  {getPrettyNumberFormat(x.copyNumber)}
                </div>
              ))}
            </div>
            <div className={columnCl}>
              <div
                className={headerCl}
                data-tooltip="Quality"
                data-tooltip-class="genome-browser-small-tooltip"
              >
                Quality
              </div>
              {analyses.map((x) => {
                const desc = getQualityText(x);
                return (
                  <div
                    data-tooltip={`Quality: ${desc} quality checks`}
                    data-tooltip-class="genome-browser-small-tooltip"
                    className={colCl}
                    key={`${x.analysis}-${x.sample}`}
                  >
                    {desc}
                  </div>
                );
              })}
            </div>
          </>
        ) : null}
        <div className={columnCl}>
          <div
            className={headerCl}
            data-tooltip="Zygosity"
            data-tooltip-class="genome-browser-small-tooltip"
          >
            Zygosity
          </div>

          {analyses.map((x) => (
            <div key={`${x.analysis}-${x.sample}`} className={colCl}>
              {x.zygosity === undefined ? (
                "-"
              ) : (
                <Zygosity
                  key={`${x.analysis}-${x.sample}`}
                  zygosity={x.zygosity}
                />
              )}
            </div>
          ))}
        </div>

        <div className="tw-h-3" />
      </div>
    </div>
  );
}

CnvTitle.propTypes = {
  varsomeHost: PropTypes.string.isRequired,
  item: PropTypes.instanceOf(ClinicalSampleCnv).isRequired,
  actions: PropTypes.node,
  track: PropTypes.instanceOf(Track).isRequired,
  requestClose: PropTypes.func,
};

CnvTitle.defaultProps = {
  requestClose: () => {},
  actions: null,
};

const mapStateToProps = (state) => ({
  varsomeHost: getVarsomeHost(state),
});
export default connect(mapStateToProps)(CnvTitle);
